import React from "react";
import { useMeasure } from "react-use";

import { cx } from "~/tools/class-variance";
import type { Time } from "~/tools/date-time/time";
import { useScope } from "~/tools/impulse";
import { mergeRefs } from "~/tools/merge-refs";
import { usePermanent } from "~/tools/use-permanent";

import type { TimeRange } from "../../app/state/view/timeline/TimeRange";
import { Layer, Stage, type StageProps } from "../_canvas";
import { useTimestampCursorState } from "../TimestampCursor";

import {
	type FormatTimeParams,
	TimelineControls,
	type TimelineControlsProps,
	TimelineGlobalCursor,
	TimelineMarkers,
	TimelineTimestamp,
	TopologyTimeMarker,
	formatTime,
} from "./_TimelineControls";
import { TimelineProvider } from "./_timeline-provider";
import { TimelineState } from "./_timeline-state";

type TimelineTimestampProps =
	| {
			/**
			 * If `true` hides TimelineHoverTimestamp
			 *
			 * @default false
			 */
			hideTimestamp: boolean;

			timestamp?: Time;
	  }
	| {
			timestamp: undefined | Time;
	  };

interface TimelineBrushProp {
	areaOffsetTop?: number;
	areaOffsetBottom?: number;
	preserveHistory?: boolean;
	onBrush: NonNullable<TimelineControlsProps["onBrush"]>;
}

export type TimelineProps = Pick<StageProps, "width" | "height"> &
	TimelineTimestampProps & {
		/**
		 * @default "canvas"
		 */
		stage?: "canvas" | "div";
		className?: string;
		style?: React.CSSProperties;

		/**
		 * If `true` makes the timeline "freeze" in time and update only when the new data comes in.
		 * The only exception is TimelineTimestamp which continues to tick in live mode
		 *
		 * @default true
		 */
		frozen?: boolean;

		/**
		 * Disable clicking and brushing
		 *
		 * @default false
		 */
		disabled?: boolean;

		/**
		 * If `true` hides TimelineHoverTimestamp
		 *
		 * @default false
		 */
		hideHoverTimestamp?: boolean;

		/**
		 * @default false
		 */
		hideMarkers?: boolean;

		/**
		 * @default false
		 */
		hideGlobalCursor?: boolean;

		/**
		 * @default false
		 */
		hideTopologyTimeMarker?: boolean;

		controlsOffsetTop?: number;
		controlsOffsetBottom?: number;

		markersOffsetTop?: number;
		markersOffsetBottom?: number;

		timeRange: TimeRange;
		children: React.ReactNode;
		state?: TimelineState;
		brush?: false | TimelineBrushProp;
		onClick?: TimelineControlsProps["onClick"];
		formatTimestamp?: (params: FormatTimeParams, px: number) => string;

		/**
		 * @default false
		 */
		compactTimestamp?: boolean;
	};

export const Timeline = React.forwardRef<HTMLDivElement, TimelineProps>(
	(
		{
			stage = "canvas",
			className,
			frozen = true,
			disabled,
			timestamp,
			timeRange,
			children,
			width,
			height,
			hideHoverTimestamp = false,
			hideMarkers = false,
			hideGlobalCursor = false,
			hideTopologyTimeMarker = false,
			controlsOffsetTop = 0,
			controlsOffsetBottom = 0,
			markersOffsetTop = controlsOffsetTop,
			markersOffsetBottom = controlsOffsetBottom,
			onClick,
			state: stateFromProps,
			brush,
			formatTimestamp = formatTime,
			compactTimestamp = false,
			...props
		},
		ref,
	) => {
		const scope = useScope();
		const hideTimestamp = "hideTimestamp" in props && props.hideTimestamp;
		const { areaOffsetTop, areaOffsetBottom, preserveHistory, onBrush } = brush || {};
		const stageRef = React.useRef<null | HTMLDivElement>(null);
		const [stageMeasureRef, { width: stageWidth }] = useMeasure<HTMLDivElement>();

		const fallbackState = usePermanent(() => new TimelineState());
		const state = stateFromProps ?? fallbackState;

		const timestampCursorState = useTimestampCursorState();

		const isBrushing = state.controls.isBrushing(scope);
		// disable user interactions while brushing or toggling a marker
		const isStageListening = !(isBrushing || timestampCursorState.isHotkeyPressed(scope));

		return (
			<div
				onMouseLeave={() => state.controls.leaveTimeline()}
				ref={ref}
				{...props}
				className={cx("relative", isBrushing && "pointer-events-none cursor-ew-resize", className)}
			>
				<TimelineProvider
					eventBucketsState={state.eventBuckets}
					formatTimestamp={formatTimestamp}
					frozen={frozen}
					lineState={state.lineState}
					stageWidth={stageWidth}
					timeRange={timeRange}
					timestamp={timestamp}
				>
					{stage === "canvas" ? (
						<Stage
							height={height}
							listening={isStageListening}
							onMouseEnter={({ evt: event }) => {
								state.controls.enterTimeline(event.clientX, getBoundingClientLeft(stageRef.current));
							}}
							ref={mergeRefs([stageRef, stageMeasureRef])}
							width={width}
						>
							<Layer>{children}</Layer>
						</Stage>
					) : (
						<div
							onMouseEnter={(event) => {
								state.controls.enterTimeline(event.clientX, getBoundingClientLeft(stageRef.current));
							}}
							ref={mergeRefs([stageRef, stageMeasureRef])}
							style={{ width, height }}
						>
							{children}
						</div>
					)}

					<div
						className="pointer-events-none absolute select-none"
						style={{ top: markersOffsetTop, bottom: markersOffsetBottom }}
					>
						{!hideGlobalCursor && <TimelineGlobalCursor />}
						{!hideMarkers && <TimelineMarkers />}
						{!hideTopologyTimeMarker && <TopologyTimeMarker />}
					</div>

					<div
						className="pointer-events-none absolute select-none"
						style={{ top: controlsOffsetTop, bottom: controlsOffsetBottom }}
					>
						{!hideTimestamp && <TimelineTimestamp compact={compactTimestamp} />}

						<TimelineControls
							brushAreaOffsetBottom={areaOffsetBottom}
							brushAreaOffsetTop={areaOffsetTop}
							hideHoverTimestamp={hideHoverTimestamp || state.eventBuckets.isBucketPopupHovered(scope)}
							hideMarkers={hideMarkers}
							onBrush={!disabled && onBrush}
							onClick={!disabled && onClick}
							preserveHistory={preserveHistory}
							state={state.controls}
						/>
					</div>
				</TimelineProvider>
			</div>
		);
	},
);

const getBoundingClientLeft = (container: null | HTMLElement) => {
	if (container == null) {
		return 0;
	}

	const { left } = container.getBoundingClientRect();

	return left;
};
